// customer-reviews-blog-api.js
// Performance-optimized blog article loader using Shopify JSON API

class CustomerReviewsAPI {
  constructor(section, config) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.config = config;
    this.grid = section.querySelector(`#reviews-grid-${this.sectionId}`);
    this.loadMoreBtn = section.querySelector(`#load-more-${this.sectionId}`);
    
    if (!this.loadMoreBtn || !this.grid) return;
    
    // State management
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMoreReviews = true;
    this.loadedArticleIds = new Set();
    
    // Cache for better performance
    this.reviewsCache = new Map();
    
    // Initialize existing review IDs to avoid duplicates
    this.initializeLoadedReviews();
    
    // Bind events
    this.init();
  }
  
  init() {
    // Load more button click
    this.loadMoreBtn.addEventListener('click', () => this.loadMoreReviews());
    
    // Prefetch next page when user is near the button
    if ('IntersectionObserver' in window) {
      this.setupIntersectionObserver();
    }
    
    // Check if we need to hide the button initially
    this.checkInitialButtonState();
  }
  
  initializeLoadedReviews() {
    // Track already loaded reviews to prevent duplicates
    const existingReviews = this.grid.querySelectorAll('.customer-reviews-blog--review');
    existingReviews.forEach((review, index) => {
      // Add a data attribute to track loaded reviews
      review.dataset.reviewIndex = index;
      this.loadedArticleIds.add(index);
    });
  }
  
  async checkInitialButtonState() {
    // Quick check if there are more reviews available
    try {
      const response = await fetch(`${window.location.pathname.match(/^\/[a-z]{2}\b/i)?.[0] || ''}/blogs/avis?page=2`);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const reviews = doc.querySelectorAll('.customer-reviews-blog--review');
      
      if (!reviews || reviews.length === 0) {
        this.hideLoadMoreButton();
        this.hasMoreReviews = false;
      }
    } catch (error) {
      console.error('Failed to check reviews availability:', error);
    }
  }
  
  setupIntersectionObserver() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading && this.hasMoreReviews) {
            this.prefetchNextPage();
          }
        });
      },
      { rootMargin: '200px' }
    );
    
    observer.observe(this.loadMoreBtn);
  }
  
  async loadMoreReviews() {
    if (this.isLoading || !this.hasMoreReviews) return;
    
    this.isLoading = true;
    this.setLoadingState(true);
    
    try {
      // Increment page for next batch
      this.currentPage++;
      
      // Check cache first
      const cacheKey = `page-${this.currentPage}`;
      let articles;
      
      if (this.reviewsCache.has(cacheKey)) {
        articles = this.reviewsCache.get(cacheKey);
      } else {
        // Fetch from API
        articles = await this.fetchReviews(this.currentPage);
        
        // Cache the results
        if (articles && articles.length > 0) {
          this.reviewsCache.set(cacheKey, articles);
        }
      }
      
      console.log('Loaded articles:', articles); // Debug log
      
      if (articles && articles.length > 0) {
        // Render the new reviews
        this.renderReviews(articles);
        
        // Check if we have more reviews
        if (articles.length < this.config.loadMoreCount) {
          this.hasMoreReviews = false;
          this.hideLoadMoreButton();
        }
      } else {
        // No more reviews
        this.hasMoreReviews = false;
        this.hideLoadMoreButton();
      }
      
    } catch (error) {
      console.error('Failed to load reviews:', error);
      this.showErrorMessage();
    } finally {
      this.setLoadingState(false);
      this.isLoading = false;
    }
  }
  
  async fetchReviews(page) {
    // Fetch the blog page directly and parse the HTML
    const url = `${window.location.pathname.match(/^\/[a-z]{2}\b/i)?.[0] || ''}/blogs/avis?page=${page}`;
    
    console.log('Fetching reviews from:', url); // Debug log
    
    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      console.log('Received HTML length:', html.length); // Debug log
      
      const articles = this.parseReviewsFromBlogHtml(html);
      
      return articles;
      
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      return [];
    }
  }
  
  parseReviewsFromBlogHtml(html) {
    // Create a temporary DOM to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const reviews = [];
    
    // Find all review elements in the blog page
    // Try multiple selectors to find reviews
    let reviewElements = doc.querySelectorAll('.customer-reviews-blog--review');
    
    // If no reviews found with that selector, try alternatives
    if (reviewElements.length === 0) {
      reviewElements = doc.querySelectorAll('.customer-reviews-grid .columns > div');
    }
    
    console.log('Found review elements:', reviewElements.length); // Debug log
    
    reviewElements.forEach((element, index) => {
      // Check if this element is actually a review container
      const reviewContainer = element.classList.contains('customer-reviews-blog--review') 
        ? element 
        : element.querySelector('.customer-reviews-blog--review');
      
      if (!reviewContainer) {
        console.log('No review container found in element', index);
        return;
      }
      
      // Extract star rating
      const starElement = reviewContainer.querySelector('.star-rating');
      let starRating = 5;
      if (starElement) {
        const styleAttr = starElement.getAttribute('style');
        if (styleAttr) {
          const match = styleAttr.match(/--star-rating:\s*([\d.]+)/);
          if (match) {
            starRating = parseFloat(match[1]);
          }
        }
      }
      
      // Extract customer name
      const nameElement = reviewContainer.querySelector('.customer-reviews-blog--author-name');
      const customerName = nameElement ? nameElement.textContent.trim() : 'Avis Google';
      
      // Extract title
      const titleElement = reviewContainer.querySelector('.customer-reviews-blog--title');
      const title = titleElement ? titleElement.textContent.trim() : '';
      
      // Extract content
      const contentElement = reviewContainer.querySelector('.customer-reviews-blog--text');
      const content = contentElement ? contentElement.textContent.trim() : '';
      
      // Extract date
      const dateElement = reviewContainer.querySelector('.customer-reviews-blog--date');
      let dateText = '';
      if (dateElement) {
        dateText = dateElement.textContent.trim();
      }
      
      // Only add if we have some content
      if (content || title) {
        // Create article object that mimics Shopify article structure
        reviews.push({
          id: `review-${Date.now()}-${index}`,
          title: title,
          content: content,
          metafields: {
            custom: {
              avis_clients_nombre_etoile: starRating,
              avis_clients_clientname: customerName,
              avis_client_date: dateText
            }
          },
          formatted_date: dateText // Pass the already formatted date
        });
      }
    });
    
    console.log('Parsed reviews:', reviews.length); // Debug log
    
    return reviews;
  }
  
  parseFrenchDate(dateText) {
    // This function is no longer needed since we're using the formatted date directly
    return dateText;
  }
  
  async fetchReviewsFallback(page) {
    // This is already our main method, no need for fallback
    return [];
  }
  
  renderReviews(articles) {
    articles.forEach((article, index) => {
      // Skip if already loaded (prevent duplicates)
      if (this.loadedArticleIds.has(article.id)) return;
      
      this.loadedArticleIds.add(article.id);
      
      // Create review element
      const reviewEl = this.createReviewElement(article, index);
      
      // Add with animation
      reviewEl.style.opacity = '0';
      reviewEl.style.transform = 'translateY(20px)';
      reviewEl.style.transition = 'none';
      
      this.grid.appendChild(reviewEl);
      
      // Trigger animation after a small delay
      requestAnimationFrame(() => {
        setTimeout(() => {
          reviewEl.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
          reviewEl.style.opacity = '1';
          reviewEl.style.transform = 'translateY(0)';
        }, index * 50);
      });
    });
  }
  
  createReviewElement(article, index) {
    const div = document.createElement('div');
    div.className = 'customer-reviews-blog--review';
    div.dataset.reviewIndex = this.loadedArticleIds.size + index;
    
    // Extract data from metafields or use defaults
    const starRating = article.metafields?.custom?.avis_clients_nombre_etoile || 5;
    const customerName = article.metafields?.custom?.avis_clients_clientname || 'Avis Google';
    const formattedDate = article.formatted_date || this.formatDate(new Date());
    
    // Build HTML structure matching the review-card snippet
    let html = '<div class="customer-reviews-blog--content">';
    
    // Star rating
    if (starRating) {
      html += `<div class="star-rating" style="--star-rating: ${starRating};"></div>`;
    }
    
    // Author info
    html += `
      <div class="customer-reviews-blog--author">
        <div class="customer-reviews-blog--author-info">
          <span class="customer-reviews-blog--author-name">${this.escapeHtml(customerName)}</span>
          <span class="customer-reviews-blog--verified">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 6L5.5 7L7.5 5M10.5 6C10.5 8.48528 8.48528 10.5 6 10.5C3.51472 10.5 1.5 8.48528 1.5 6C1.5 3.51472 3.51472 1.5 6 1.5C8.48528 1.5 10.5 3.51472 10.5 6Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Vérifié
          </span>
        </div>
      </div>`;
    
    // Title
    if (article.title) {
      html += `<h6 class="customer-reviews-blog--title">${this.escapeHtml(article.title)}</h6>`;
    }
    
    // Content
    const content = this.stripHtml(article.content || '');
    const excerpt = content.substring(0, 300) + (content.length > 300 ? '...' : '');
    html += `<div class="customer-reviews-blog--text">${this.escapeHtml(excerpt)}</div>`;
    
    // Date
    html += `<div class="customer-reviews-blog--date">${formattedDate}</div>`;
    
    html += '</div>';
    
    div.innerHTML = html;
    
    return div;
  }
  
  async prefetchNextPage() {
    // Silently prefetch the next page for instant loading
    const nextPage = this.currentPage + 1;
    const cacheKey = `page-${nextPage}`;
    
    // Don't prefetch if already cached
    if (this.reviewsCache.has(cacheKey)) return;
    
    try {
      const articles = await this.fetchReviews(nextPage);
      if (articles && articles.length > 0) {
        this.reviewsCache.set(cacheKey, articles);
      }
    } catch (error) {
      // Silent fail for prefetch
      console.debug('Prefetch failed:', error);
    }
  }
  
  setLoadingState(loading) {
    const buttonText = this.loadMoreBtn.querySelector('.button-text');
    const loadingSpinner = this.loadMoreBtn.querySelector('.loading-spinner');
    
    if (loading) {
      buttonText.style.display = 'none';
      loadingSpinner.style.display = 'inline-block';
      this.loadMoreBtn.disabled = true;
    } else {
      buttonText.style.display = 'inline';
      loadingSpinner.style.display = 'none';
      this.loadMoreBtn.disabled = false;
    }
  }
  
  hideLoadMoreButton() {
    this.loadMoreBtn.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    this.loadMoreBtn.style.opacity = '0';
    this.loadMoreBtn.style.transform = 'translateY(10px)';
    
    setTimeout(() => {
      this.loadMoreBtn.style.display = 'none';
    }, 300);
  }
  
  showErrorMessage() {
    // Create a temporary error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'customer-reviews-blog--error';
    errorDiv.style.cssText = `
      text-align: center;
      padding: 20px;
      color: #dc2626;
      font-size: 0.875rem;
    `;
    errorDiv.textContent = 'Erreur lors du chargement des avis. Veuillez réessayer.';
    
    this.loadMoreBtn.parentElement.appendChild(errorDiv);
    
    // Remove after 3 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 3000);
  }
  
  formatDate(date) {
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    const months = [
      'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
      'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
    ];
    
    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Hier";
    if (diffDays <= 7) return `Il y a ${diffDays} jours`;
    if (diffDays <= 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "Il y a 1 semaine" : `Il y a ${weeks} semaines`;
    }
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  }
  
  stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
function initializeReviews() {
  const sections = document.querySelectorAll('customer-reviews-blog');
  
  sections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    const config = window.reviewsConfig && window.reviewsConfig[sectionId];
    
    if (config) {
      new CustomerReviewsAPI(section, config);
    }
  });
}

// Start initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeReviews);
} else {
  initializeReviews();
}

// Export for potential reuse
window.CustomerReviewsAPI = CustomerReviewsAPI;